// @flow
import ModalChangePassword from 'components/Profile/ModalChangePassword'
import keys from 'lodash/keys'
import reduce from 'lodash/reduce'
import some from 'lodash/some'
import toPairs from 'lodash/toPairs'
import trim from 'lodash/trim'
import * as React from 'react'
import { toast } from 'react-toastify'
import Ui from 'ui'
import Button from 'ui/atoms/Button'
import FlatCard from 'ui/atoms/FlatCard'
import Flex from 'ui/atoms/Flex'
import FlexBox from 'ui/atoms/FlexBox'
import Space from 'ui/atoms/Space'
import TextField from 'ui/molecules/TextField'
import { hasText, isFilledArray } from 'utils/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

import { useUpdateUserDetails } from '@/queries/users/useUpdateUsers'

import ProfileTabMFA from './components/ProfileTabMFA'
import ProfileTabSignatureFields from './components/ProfileTabSignatureFields'
function ProfileTab(): React.Node {
  const { updateUserDetails } = useUpdateUserDetails()
  const { userDetails } = React.useContext(ZentakeContext)
  const [isChangingPassword, setIsChangingPassword] = React.useState(false)

  const [newUserDetails, setNewUserDetails] = React.useState(userDetails)
  const [willUpdate, setWillUpdate] = React.useState(false)
  const [isUpdatingProfile, setUpdatingProfile] = React.useState(false)

  React.useEffect(() => {
    if (!userDetails) return

    setWillUpdate(
      some(toPairs(newUserDetails), ([key, value]) => {
        if (['provider_signature', 'provider_initials'].includes(key)) {
          return userDetails[key] !== value
        }

        return hasText(value) && userDetails[key] !== trim(value)
      })
    )
  }, [userDetails, newUserDetails])

  const onUpdateProfileData = React.useCallback(async () => {
    setUpdatingProfile(true)
    if (!userDetails) return

    try {
      const detailsToUpdate = reduce(
        toPairs(newUserDetails),
        (obj, [key, value]) => {
          if (['provider_signature', 'provider_initials'].includes(key)) {
            if (userDetails[key] !== value) {
              return { ...obj, [key]: value }
            }

            return obj
          }

          if (hasText(value) && trim(userDetails[key]) !== trim(value)) {
            return { ...obj, [key]: trim(value) }
          }
          return obj
        },
        {}
      )

      if (!isFilledArray(keys(detailsToUpdate))) {
        setUpdatingProfile(false)
        return
      }
      const userDetail = await updateUserDetails(detailsToUpdate)
      setNewUserDetails(userDetail)
      toast.success('Profile data updated')
    } catch (error) {
      toast.error(error.message)
    } finally {
      setUpdatingProfile(false)
    }
  }, [userDetails, newUserDetails, updateUserDetails])

  const updateFirstName = React.useCallback(
    (first_name) => setNewUserDetails((p) => (p ? { ...p, first_name } : p)),
    []
  )

  const updateLastName = React.useCallback(
    (last_name) => setNewUserDetails((p) => (p ? { ...p, last_name } : p)),
    []
  )

  return (
    <FlatCard>
      <Space size="large">
        <Flex
          $flexDirection={{ default: 'col', lg: 'row' }}
          $horizontalSpacing={{ default: 'none', lg: 'large' }}
          $verticalSpacing={{ default: 'medium', lg: 'none' }}
        >
          <FlexBox>
            <TextField
              block
              label="First name"
              name="first_name"
              onChange={updateFirstName}
              placeholder="First name"
              type="text"
              value={newUserDetails?.first_name || ''}
            />
          </FlexBox>
          <FlexBox>
            <TextField
              block
              label="Last name"
              name="last_name"
              onChange={updateLastName}
              placeholder="Last name"
              type="text"
              value={newUserDetails?.last_name || ''}
            />
          </FlexBox>
        </Flex>
        <Flex
          $flexDirection={{ default: 'col', lg: 'row' }}
          $horizontalSpacing={{ default: 'none', lg: 'large' }}
          $verticalSpacing={{ default: 'medium', lg: 'none' }}
        >
          <FlexBox>
            <TextField
              block
              disabled
              grayOutWhenDisabled
              label="Email"
              name="email"
              placeholder="User email"
              type="email"
              value={newUserDetails?.email || ''}
            />
          </FlexBox>
          <FlexBox>
            <Ui.PasswordInput
              block
              disabled
              grayOutWhenDisabled
              pencil
              label="Password"
              name="password"
              onIconClick={() => {
                setIsChangingPassword(true)
              }}
              placeholder="Password"
              type="password"
              value="••••••••••••••••"
            />
          </FlexBox>
        </Flex>
        {userDetails?.team?.require_2fa && <ProfileTabMFA />}
        <div className="lg:w-1/2">
          <ProfileTabSignatureFields
            // $FlowIgnore
            setUserDetails={setNewUserDetails}
            // $FlowIgnore
            userDetails={newUserDetails}
          />
        </div>
        <Button
          $primary
          disabled={!willUpdate}
          loading={isUpdatingProfile}
          onClick={onUpdateProfileData}
        >
          Update profile
        </Button>
      </Space>

      {isChangingPassword && (
        <ModalChangePassword
          onClose={() => setIsChangingPassword(false)}
          onPasswordChanged={() => setIsChangingPassword(false)}
        />
      )}
    </FlatCard>
  )
}

export default ProfileTab
